import React, { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import ReactFlow, {
  addEdge,
  Background,
  Controls,
  MiniMap,
  useNodesState,
  useEdgesState,
  MarkerType,
  Panel,
  Handle,
  Position,
  NodeResizer,
  ReactFlowProvider,
  useReactFlow,
} from 'reactflow';
import 'reactflow/dist/style.css';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import {
  Shield, Plus, Play, Download, Trash2, Save, Loader2, ChevronRight,
  AlertTriangle, Server, Database, Globe, Lock, Eye, X,
  CheckCircle2, ShieldAlert, FileJson, GripVertical, FileText,
  ArrowLeftRight,
} from 'lucide-react';
import api from '@/services/api';

// ========================
// STRIDE Color Config
// ========================
const STRIDE_CONFIG = {
  S: { name: 'Spoofing', color: '#ef4444', bg: 'bg-red-100', text: 'text-red-700', border: 'border-red-300' },
  T: { name: 'Tampering', color: '#f97316', bg: 'bg-orange-100', text: 'text-orange-700', border: 'border-orange-300' },
  R: { name: 'Repudiation', color: '#eab308', bg: 'bg-yellow-100', text: 'text-yellow-700', border: 'border-yellow-300' },
  I: { name: 'Info Disclosure', color: '#3b82f6', bg: 'bg-blue-100', text: 'text-blue-700', border: 'border-blue-300' },
  D: { name: 'Denial of Service', color: '#8b5cf6', bg: 'bg-violet-100', text: 'text-violet-700', border: 'border-violet-300' },
  E: { name: 'Elevation of Privilege', color: '#dc2626', bg: 'bg-red-100', text: 'text-red-800', border: 'border-red-400' },
};

const SEVERITY_CONFIG = {
  critical: { bg: 'bg-red-600', text: 'text-white' },
  high: { bg: 'bg-orange-500', text: 'text-white' },
  medium: { bg: 'bg-yellow-500', text: 'text-black' },
  low: { bg: 'bg-green-500', text: 'text-white' },
};

// ========================
// Handle Styles
// ========================
const handleStyle = {
  width: 8,
  height: 8,
  borderRadius: '50%',
  background: '#64748b',
  border: '2px solid #fff',
  zIndex: 10,
};

const handleActiveStyle = {
  ...handleStyle,
  background: '#3b82f6',
  boxShadow: '0 0 4px rgba(59,130,246,0.5)',
};

// ========================
// Custom Node Components with Handles + Resize
// ========================
const ProcessNode = React.memo(({ data, selected }) => {
  const threatCount = data.threatCount || 0;
  const isOutOfScope = data.properties?.out_of_scope;
  const hasMitigated = data.allMitigated;
  const borderColor = isOutOfScope ? '#9ca3af' : hasMitigated ? '#22c55e' : threatCount > 0 ? '#ef4444' : '#3b82f6';

  return (
    <div
      className="relative flex items-center justify-center"
      style={{
        width: '100%',
        height: '100%',
        minWidth: 80,
        minHeight: 80,
        background: isOutOfScope ? '#f9fafb' : '#dbeafe',
        border: `2px ${isOutOfScope ? 'dashed' : 'solid'} ${borderColor}`,
        borderRadius: '50%',
        padding: 8,
        opacity: isOutOfScope ? 0.6 : 1,
      }}
    >
      <NodeResizer
        isVisible={selected}
        minWidth={80}
        minHeight={80}
        keepAspectRatio
        lineStyle={{ stroke: borderColor, strokeWidth: 1 }}
        handleStyle={{ width: 8, height: 8, borderRadius: 2, background: borderColor }}
      />
      <Handle type="target" position={Position.Left} style={selected ? handleActiveStyle : handleStyle} />
      <Handle type="target" position={Position.Top} id="top" style={selected ? handleActiveStyle : handleStyle} />
      <Handle type="source" position={Position.Right} style={selected ? handleActiveStyle : handleStyle} />
      <Handle type="source" position={Position.Bottom} id="bottom" style={selected ? handleActiveStyle : handleStyle} />
      <div className="text-center">
        <Server className="w-4 h-4 mx-auto text-blue-600 mb-0.5" />
        <div className="text-xs font-semibold leading-tight" style={{ maxWidth: 90, wordBreak: 'break-word' }}>{data.label}</div>
      </div>
      {threatCount > 0 && (
        <div className="absolute -top-2 -right-2 text-white text-[10px] font-bold rounded-full w-5 h-5 flex items-center justify-center" style={{ background: hasMitigated ? '#22c55e' : '#ef4444' }}>{threatCount}</div>
      )}
      {isOutOfScope && <div className="absolute -bottom-4 text-[9px] text-gray-400 whitespace-nowrap">out of scope</div>}
    </div>
  );
});

const DataStoreNode = React.memo(({ data, selected }) => {
  const threatCount = data.threatCount || 0;
  const isOutOfScope = data.properties?.out_of_scope;
  const hasMitigated = data.allMitigated;
  const borderColor = isOutOfScope ? '#9ca3af' : hasMitigated ? '#22c55e' : threatCount > 0 ? '#ef4444' : '#f59e0b';

  return (
    <div
      className="relative flex items-center justify-center"
      style={{
        width: '100%',
        height: '100%',
        minWidth: 100,
        minHeight: 40,
        background: isOutOfScope ? '#f9fafb' : '#fef3c7',
        border: `2px ${isOutOfScope ? 'dashed' : 'solid'} ${borderColor}`,
        borderRadius: 4,
        borderTop: `3px ${isOutOfScope ? 'dashed' : 'solid'} ${borderColor}`,
        borderBottom: `3px ${isOutOfScope ? 'dashed' : 'solid'} ${borderColor}`,
        padding: '8px 12px',
        opacity: isOutOfScope ? 0.6 : 1,
      }}
    >
      <NodeResizer
        isVisible={selected}
        minWidth={100}
        minHeight={40}
        lineStyle={{ stroke: borderColor, strokeWidth: 1 }}
        handleStyle={{ width: 8, height: 8, borderRadius: 2, background: borderColor }}
      />
      <Handle type="target" position={Position.Left} style={selected ? handleActiveStyle : handleStyle} />
      <Handle type="target" position={Position.Top} id="top" style={selected ? handleActiveStyle : handleStyle} />
      <Handle type="source" position={Position.Right} style={selected ? handleActiveStyle : handleStyle} />
      <Handle type="source" position={Position.Bottom} id="bottom" style={selected ? handleActiveStyle : handleStyle} />
      <div className="text-center">
        <Database className="w-4 h-4 mx-auto text-amber-600 mb-0.5" />
        <div className="text-xs font-semibold leading-tight">{data.label}</div>
      </div>
      {threatCount > 0 && (
        <div className="absolute -top-2 -right-2 text-white text-[10px] font-bold rounded-full w-5 h-5 flex items-center justify-center" style={{ background: hasMitigated ? '#22c55e' : '#ef4444' }}>{threatCount}</div>
      )}
      {isOutOfScope && <div className="absolute -bottom-4 text-[9px] text-gray-400 whitespace-nowrap">out of scope</div>}
    </div>
  );
});

const ExternalEntityNode = React.memo(({ data, selected }) => {
  const threatCount = data.threatCount || 0;
  const isOutOfScope = data.properties?.out_of_scope;
  const hasMitigated = data.allMitigated;
  const borderColor = isOutOfScope ? '#9ca3af' : hasMitigated ? '#22c55e' : threatCount > 0 ? '#ef4444' : '#6b7280';

  return (
    <div
      className="relative flex items-center justify-center"
      style={{
        width: '100%',
        height: '100%',
        minWidth: 100,
        minHeight: 40,
        background: isOutOfScope ? '#f9fafb' : '#f3f4f6',
        border: `2px ${isOutOfScope ? 'dashed' : 'solid'} ${borderColor}`,
        borderRadius: 4,
        padding: '8px 12px',
        opacity: isOutOfScope ? 0.6 : 1,
      }}
    >
      <NodeResizer
        isVisible={selected}
        minWidth={100}
        minHeight={40}
        lineStyle={{ stroke: borderColor, strokeWidth: 1 }}
        handleStyle={{ width: 8, height: 8, borderRadius: 2, background: borderColor }}
      />
      <Handle type="target" position={Position.Left} style={selected ? handleActiveStyle : handleStyle} />
      <Handle type="target" position={Position.Top} id="top" style={selected ? handleActiveStyle : handleStyle} />
      <Handle type="source" position={Position.Right} style={selected ? handleActiveStyle : handleStyle} />
      <Handle type="source" position={Position.Bottom} id="bottom" style={selected ? handleActiveStyle : handleStyle} />
      <div className="text-center">
        <Globe className="w-4 h-4 mx-auto text-gray-600 mb-0.5" />
        <div className="text-xs font-semibold leading-tight">{data.label}</div>
      </div>
      {threatCount > 0 && (
        <div className="absolute -top-2 -right-2 text-white text-[10px] font-bold rounded-full w-5 h-5 flex items-center justify-center" style={{ background: hasMitigated ? '#22c55e' : '#ef4444' }}>{threatCount}</div>
      )}
      {isOutOfScope && <div className="absolute -bottom-4 text-[9px] text-gray-400 whitespace-nowrap">out of scope</div>}
    </div>
  );
});

const TrustBoundaryNode = React.memo(({ data, selected }) => {
  const threatCount = data.threatCount || 0;
  const isOutOfScope = data.properties?.out_of_scope;
  const hasMitigated = data.allMitigated;
  const borderColor = isOutOfScope ? '#9ca3af' : hasMitigated ? '#22c55e' : threatCount > 0 ? '#ef4444' : '#ef4444';

  return (
    <div
      className="relative flex items-center justify-center"
      style={{
        width: '100%',
        height: '100%',
        minWidth: 120,
        minHeight: 50,
        background: isOutOfScope ? 'rgba(156,163,175,0.05)' : 'rgba(239,68,68,0.06)',
        border: `2px dashed ${borderColor}`,
        borderRadius: 12,
        padding: '8px 12px',
        opacity: isOutOfScope ? 0.6 : 1,
      }}
    >
      <NodeResizer
        isVisible={selected}
        minWidth={120}
        minHeight={50}
        lineStyle={{ stroke: borderColor, strokeWidth: 1, strokeDasharray: '5 3' }}
        handleStyle={{ width: 8, height: 8, borderRadius: 2, background: borderColor }}
      />
      <Handle type="target" position={Position.Left} style={selected ? handleActiveStyle : handleStyle} />
      <Handle type="target" position={Position.Top} id="top" style={selected ? handleActiveStyle : handleStyle} />
      <Handle type="source" position={Position.Right} style={selected ? handleActiveStyle : handleStyle} />
      <Handle type="source" position={Position.Bottom} id="bottom" style={selected ? handleActiveStyle : handleStyle} />
      <div className="text-center">
        <Lock className="w-4 h-4 mx-auto text-red-500 mb-0.5" />
        <div className="text-xs font-semibold leading-tight text-red-700">{data.label}</div>
      </div>
      {threatCount > 0 && (
        <div className="absolute -top-2 -right-2 text-white text-[10px] font-bold rounded-full w-5 h-5 flex items-center justify-center" style={{ background: hasMitigated ? '#22c55e' : '#ef4444' }}>{threatCount}</div>
      )}
      {isOutOfScope && <div className="absolute -bottom-4 text-[9px] text-gray-400 whitespace-nowrap">out of scope</div>}
    </div>
  );
});

// ========================
// Property Definitions per Node Type
// ========================
const PROPERTY_DEFS = {
  process: [
    { key: 'out_of_scope', label: 'Out of Scope', type: 'bool' },
    { key: 'authentication', label: 'Authentication', type: 'bool' },
    { key: 'authorization', label: 'Authorization', type: 'bool' },
    { key: 'input_validation', label: 'Input Validation', type: 'bool' },
    { key: 'audit_logging', label: 'Audit Logging', type: 'bool' },
    { key: 'handles_sensitive_data', label: 'Handles Sensitive Data', type: 'bool' },
    { key: 'rate_limiting', label: 'Rate Limiting', type: 'bool' },
    { key: 'runs_elevated', label: 'Runs Elevated', type: 'bool' },
    { key: 'accepts_external_input', label: 'Accepts External Input', type: 'bool' },
    { key: 'modifies_data', label: 'Modifies Data', type: 'bool' },
  ],
  datastore: [
    { key: 'out_of_scope', label: 'Out of Scope', type: 'bool' },
    { key: 'authentication', label: 'Access Authentication', type: 'bool' },
    { key: 'encrypted_at_rest', label: 'Encrypted at Rest', type: 'bool' },
    { key: 'write_protected', label: 'Write Protected', type: 'bool' },
    { key: 'audit_trail', label: 'Audit Trail', type: 'bool' },
    { key: 'backup_enabled', label: 'Backup Enabled', type: 'bool' },
    { key: 'contains_pii', label: 'Contains PII', type: 'bool' },
    { key: 'row_level_security', label: 'Row-Level Security', type: 'bool' },
  ],
  external_entity: [
    { key: 'out_of_scope', label: 'Out of Scope', type: 'bool' },
    { key: 'verified', label: 'Identity Verified', type: 'bool' },
    { key: 'input_validated', label: 'Input Validated', type: 'bool' },
    { key: 'interactions_logged', label: 'Interactions Logged', type: 'bool' },
    { key: 'receives_sensitive_data', label: 'Receives Sensitive Data', type: 'bool' },
    { key: 'over_trusted', label: 'Over-Trusted', type: 'bool' },
  ],
  trust_boundary: [
    { key: 'out_of_scope', label: 'Out of Scope', type: 'bool' },
    { key: 'boundary_auth', label: 'Boundary Authentication', type: 'bool' },
    { key: 'boundary_validation', label: 'Boundary Validation', type: 'bool' },
    { key: 'boundary_encrypted', label: 'Boundary Encrypted', type: 'bool' },
    { key: 'boundary_filtering', label: 'Boundary Filtering', type: 'bool' },
    { key: 'privilege_escalation_possible', label: 'Privilege Escalation Possible', type: 'bool' },
  ],
  dataflow: [
    { key: 'encrypted', label: 'Encrypted in Transit', type: 'bool' },
    { key: 'source_authenticated', label: 'Source Authenticated', type: 'bool' },
    { key: 'integrity_check', label: 'Integrity Check', type: 'bool' },
    { key: 'logged', label: 'Logged', type: 'bool' },
    { key: 'carries_sensitive_data', label: 'Carries Sensitive Data', type: 'bool' },
    { key: 'flow_control', label: 'Flow Control', type: 'bool' },
    { key: 'bidirectional', label: 'Bidirectional Flow', type: 'bool' },
  ],
};

// ========================
// Stencil Palette
// ========================
const STENCIL_ITEMS = [
  { type: 'process', label: 'Process', icon: Server, color: '#3b82f6', bgColor: '#dbeafe', desc: 'Code execution unit' },
  { type: 'datastore', label: 'Data Store', icon: Database, color: '#f59e0b', bgColor: '#fef3c7', desc: 'Persistent storage' },
  { type: 'external_entity', label: 'External Entity', icon: Globe, color: '#6b7280', bgColor: '#f3f4f6', desc: 'Actor outside system' },
  { type: 'trust_boundary', label: 'Trust Boundary', icon: Lock, color: '#ef4444', bgColor: 'rgba(239,68,68,0.08)', desc: 'Security zone border' },
];

const StencilPalette = () => {
  const onDragStart = (event, nodeType) => {
    event.dataTransfer.setData('application/reactflow', nodeType);
    event.dataTransfer.effectAllowed = 'move';
  };

  return (
    <div className="w-[180px] bg-white border-r shrink-0 flex flex-col" data-testid="stencil-palette">
      <div className="p-3 border-b bg-slate-50">
        <h3 className="text-xs font-semibold text-gray-700 uppercase tracking-wide">DFD Stencils</h3>
        <p className="text-[10px] text-gray-400 mt-0.5">Drag onto canvas</p>
      </div>
      <div className="p-2 space-y-1.5 flex-1 overflow-y-auto">
        {STENCIL_ITEMS.map((item) => {
          const Icon = item.icon;
          return (
            <div
              key={item.type}
              className="flex items-center gap-2 p-2 rounded-lg border border-gray-200 cursor-grab hover:border-blue-300 hover:shadow-sm transition-all active:cursor-grabbing"
              draggable
              onDragStart={(e) => onDragStart(e, item.type)}
              data-testid={`stencil-${item.type}`}
            >
              <div className="w-8 h-8 rounded flex items-center justify-center shrink-0" style={{ background: item.bgColor, border: `1.5px solid ${item.color}` }}>
                <Icon className="w-4 h-4" style={{ color: item.color }} />
              </div>
              <div className="min-w-0">
                <p className="text-xs font-medium text-gray-800 leading-tight">{item.label}</p>
                <p className="text-[10px] text-gray-400 leading-tight">{item.desc}</p>
              </div>
              <GripVertical className="w-3 h-3 text-gray-300 shrink-0 ml-auto" />
            </div>
          );
        })}
      </div>
      <div className="p-2 border-t bg-slate-50">
        <div className="space-y-1 text-[10px] text-gray-400">
          <p className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-red-500 inline-block"></span> Open threats</p>
          <p className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-green-500 inline-block"></span> All mitigated</p>
          <p className="flex items-center gap-1"><span className="w-2 h-2 border border-dashed border-gray-400 rounded-sm inline-block"></span> Out of scope</p>
        </div>
      </div>
    </div>
  );
};

// ========================
// Report Generator
// ========================
const generateHTMLReport = (model, nodes, edges, threats, summary) => {
  const strideRows = Object.entries(STRIDE_CONFIG).map(([k, v]) =>
    `<tr><td style="font-weight:600;color:${v.color}">${k}</td><td>${v.name}</td><td>${summary?.by_stride?.[k] || 0}</td></tr>`
  ).join('');

  const threatRows = threats.map(t => `
    <tr style="border-bottom:1px solid #e5e7eb">
      <td style="padding:8px"><span style="background:${STRIDE_CONFIG[t.stride_category]?.color};color:white;padding:2px 6px;border-radius:4px;font-size:11px">${t.stride_category}</span></td>
      <td style="padding:8px;font-size:12px">${t.element_label}</td>
      <td style="padding:8px;font-size:12px">${t.threat}</td>
      <td style="padding:8px"><span style="padding:2px 6px;border-radius:4px;font-size:11px;font-weight:600;background:${t.severity === 'critical' ? '#dc2626' : t.severity === 'high' ? '#f97316' : t.severity === 'medium' ? '#eab308' : '#22c55e'};color:${t.severity === 'medium' ? '#000' : '#fff'}">${t.severity}</span></td>
      <td style="padding:8px;font-size:12px">${t.dread?.average || '-'}</td>
      <td style="padding:8px;font-size:12px">${t.mitigation}</td>
      <td style="padding:8px;font-size:12px;text-transform:capitalize">${t.status}</td>
    </tr>
  `).join('');

  return `<!DOCTYPE html><html><head><meta charset="utf-8"><title>Threat Model Report: ${model?.name || 'Unnamed'}</title>
<style>body{font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif;max-width:1100px;margin:40px auto;padding:0 20px;color:#1e293b}
h1{color:#1e3a5f;border-bottom:3px solid #3b82f6;padding-bottom:12px}h2{color:#334155;margin-top:32px}
table{width:100%;border-collapse:collapse;margin:16px 0}th{background:#f1f5f9;text-align:left;padding:8px;font-size:12px;border-bottom:2px solid #e2e8f0}
td{padding:8px;font-size:13px}.summary-grid{display:grid;grid-template-columns:repeat(4,1fr);gap:16px;margin:16px 0}
.summary-card{background:#f8fafc;border:1px solid #e2e8f0;border-radius:8px;padding:16px;text-align:center}
.summary-card .number{font-size:28px;font-weight:700}.summary-card .label{font-size:12px;color:#64748b;margin-top:4px}
.footer{margin-top:40px;padding-top:16px;border-top:1px solid #e2e8f0;font-size:11px;color:#94a3b8;text-align:center}</style></head>
<body><h1>Threat Model Report</h1>
<p><strong>Model:</strong> ${model?.name || 'Unnamed'}</p>
<p><strong>Description:</strong> ${model?.description || 'N/A'}</p>
<p><strong>Generated:</strong> ${new Date().toLocaleString()}</p>
<p><strong>Elements:</strong> ${nodes.length} nodes, ${edges.length} data flows</p>

<div class="summary-grid">
<div class="summary-card"><div class="number">${summary?.total_threats || 0}</div><div class="label">Total Threats</div></div>
<div class="summary-card"><div class="number" style="color:#dc2626">${summary?.by_severity?.critical || 0}</div><div class="label">Critical</div></div>
<div class="summary-card"><div class="number" style="color:#f97316">${summary?.by_severity?.high || 0}</div><div class="label">High</div></div>
<div class="summary-card"><div class="number" style="color:#eab308">${summary?.by_severity?.medium || 0}</div><div class="label">Medium</div></div>
</div>

<h2>STRIDE Distribution</h2>
<table><thead><tr><th>Category</th><th>Name</th><th>Count</th></tr></thead><tbody>${strideRows}</tbody></table>

<h2>All Threats (${threats.length})</h2>
<table><thead><tr><th>STRIDE</th><th>Element</th><th>Threat</th><th>Severity</th><th>DREAD</th><th>Mitigation</th><th>Status</th></tr></thead><tbody>${threatRows}</tbody></table>

<div class="footer">Generated by Beskar Security Platform - STRIDE Threat Modeling Engine</div>
</body></html>`;
};

// ========================
// Build Edge helper (pure function, no state deps)
// ========================
const buildEdge = (e) => {
  const isBidi = e.data?.properties?.bidirectional;
  return {
    ...e,
    type: 'default',
    animated: true,
    markerEnd: { type: MarkerType.ArrowClosed, color: '#64748b' },
    markerStart: isBidi ? { type: MarkerType.ArrowClosed, color: '#64748b' } : undefined,
    style: { stroke: '#64748b', strokeWidth: 1.5 },
    label: e.label || e.data?.label || '',
    labelStyle: { fontSize: 10, fill: '#475569', fontWeight: 500 },
    labelBgStyle: { fill: '#f1f5f9', fillOpacity: 0.9 },
  };
};

// ========================
// Inner Flow Component (needs ReactFlowProvider context)
// ========================
const ThreatModelFlow = () => {
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);
  const [threats, setThreats] = useState([]);
  const [summary, setSummary] = useState(null);
  const [models, setModels] = useState([]);
  const [currentModel, setCurrentModel] = useState(null);
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showNewModel, setShowNewModel] = useState(false);
  const [showNodeEditor, setShowNodeEditor] = useState(false);
  const [showEdgeEditor, setShowEdgeEditor] = useState(false);
  const [editingNode, setEditingNode] = useState(null);
  const [editingEdge, setEditingEdge] = useState(null);
  const [newModelForm, setNewModelForm] = useState({ name: '', description: '' });
  const [selectedThreat, setSelectedThreat] = useState(null);
  const [threatFilter, setThreatFilter] = useState('all');
  const [rightPanelOpen, setRightPanelOpen] = useState(true);
  const [selectedNodeId, setSelectedNodeId] = useState(null);
  const reactFlowWrapper = useRef(null);
  const reactFlowInstance = useReactFlow();

  const nodeTypes = useMemo(() => ({
    process: ProcessNode,
    datastore: DataStoreNode,
    external_entity: ExternalEntityNode,
    trust_boundary: TrustBoundaryNode,
  }), []);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    loadModels();
  }, []);

  const loadModels = async () => {
    try {
      const resp = await api.get('/litesabre-pro/threat-model/models');
      setModels(resp.data.models || []);
    } catch { /* silent */ }
  };

  const computeThreatCounts = useCallback((nodesList, threatsList) => {
    const threatsByElement = {};
    const mitigatedByElement = {};
    threatsList.forEach(t => {
      threatsByElement[t.element_id] = (threatsByElement[t.element_id] || 0) + 1;
      if (!mitigatedByElement[t.element_id]) mitigatedByElement[t.element_id] = { total: 0, mitigated: 0 };
      mitigatedByElement[t.element_id].total++;
      if (t.status === 'mitigated') mitigatedByElement[t.element_id].mitigated++;
    });
    return nodesList.map(n => ({
      ...n,
      data: {
        ...n.data,
        threatCount: threatsByElement[n.id] || 0,
        allMitigated: mitigatedByElement[n.id] ? mitigatedByElement[n.id].total === mitigatedByElement[n.id].mitigated && mitigatedByElement[n.id].total > 0 : false,
      },
    }));
  }, []);

  const loadModel = async (modelId) => {
    try {
      setLoading(true);
      const resp = await api.get(`/litesabre-pro/threat-model/models/${modelId}`);
      const model = resp.data;
      setCurrentModel(model);
      const loadedNodes = (model.nodes || []).map(n => ({
        ...n,
        style: n.style || { width: n.type === 'process' ? 120 : n.type === 'trust_boundary' ? 160 : 130, height: n.type === 'process' ? 120 : 60 },
      }));
      const updatedNodes = computeThreatCounts(loadedNodes, model.threats || []);
      setNodes(updatedNodes);
      setEdges((model.edges || []).map(buildEdge));
      setThreats(model.threats || []);
      setSummary(model.summary || null);
      setSelectedNodeId(null);
    } catch {
      toast.error('Failed to load threat model');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateModel = async () => {
    if (!newModelForm.name) { toast.error('Name is required'); return; }
    try {
      setLoading(true);
      const resp = await api.post('/litesabre-pro/threat-model/models', { name: newModelForm.name, description: newModelForm.description });
      toast.success('Threat model created');
      setShowNewModel(false);
      setNewModelForm({ name: '', description: '' });
      await loadModels();
      await loadModel(resp.data.id);
    } catch {
      toast.error('Failed to create threat model');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteModel = async (modelId) => {
    try {
      await api.delete(`/litesabre-pro/threat-model/models/${modelId}`);
      toast.success('Threat model deleted');
      if (currentModel?.id === modelId) {
        setCurrentModel(null); setNodes([]); setEdges([]); setThreats([]); setSummary(null);
      }
      await loadModels();
    } catch { toast.error('Failed to delete'); }
  };

  const handleGenerateThreats = async () => {
    if (nodes.length === 0) { toast.error('Add elements to the diagram first'); return; }
    try {
      setGenerating(true);
      const payload = {
        nodes: nodes.filter(n => !n.data.properties?.out_of_scope).map(n => ({
          id: n.id, type: n.type, position: n.position,
          data: { label: n.data.label, properties: n.data.properties || {} },
        })),
        edges: edges.map(e => ({
          id: e.id, source: e.source, target: e.target, label: e.label || '',
          data: { label: e.label || e.data?.label || '', properties: e.data?.properties || {} },
        })),
      };
      const resp = await api.post('/litesabre-pro/threat-model/generate-threats', payload);
      const newThreats = resp.data.threats || [];
      setThreats(newThreats);
      setSummary(resp.data.summary || null);
      setNodes(nds => computeThreatCounts(nds, newThreats));
      toast.success(`Generated ${newThreats.length} STRIDE threats`);
      setRightPanelOpen(true);
    } catch { toast.error('Failed to generate threats'); }
    finally { setGenerating(false); }
  };

  const handleSave = async () => {
    if (!currentModel) return;
    try {
      setSaving(true);
      await api.put(`/litesabre-pro/threat-model/models/${currentModel.id}`, {
        nodes: nodes.map(n => ({ id: n.id, type: n.type, position: n.position, style: n.style, data: { label: n.data.label, properties: n.data.properties || {} } })),
        edges: edges.map(e => ({ id: e.id, source: e.source, target: e.target, label: e.label || '', data: { label: e.label || e.data?.label || '', properties: e.data?.properties || {} } })),
      });
      toast.success('Model saved');
      await loadModels();
    } catch { toast.error('Failed to save'); }
    finally { setSaving(false); }
  };

  const handleThreatStatusUpdate = async (threatId, newStatus) => {
    if (!currentModel) return;
    try {
      await api.put(`/litesabre-pro/threat-model/models/${currentModel.id}/threats/${threatId}/status`, { status: newStatus });
      const updated = threats.map(t => t.id === threatId ? { ...t, status: newStatus } : t);
      setThreats(updated);
      setNodes(nds => computeThreatCounts(nds, updated));
      toast.success(`Threat marked as ${newStatus}`);
    } catch { toast.error('Failed to update status'); }
  };

  const onConnect = useCallback((params) => {
    const newEdge = buildEdge({
      ...params,
      id: `df-${Date.now()}`,
      data: { label: 'Data Flow', properties: { encrypted: false, source_authenticated: false, integrity_check: false, logged: false, carries_sensitive_data: false, flow_control: false, bidirectional: false } },
      label: 'Data Flow',
    });
    setEdges(eds => addEdge(newEdge, eds));
  }, [setEdges]);

  // Drag-and-drop from stencil palette
  const onDragOver = useCallback((event) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';
  }, []);

  const onDrop = useCallback((event) => {
    event.preventDefault();
    const type = event.dataTransfer.getData('application/reactflow');
    if (!type || !reactFlowWrapper.current) return;

    const bounds = reactFlowWrapper.current.getBoundingClientRect();
    const position = reactFlowInstance.project({ x: event.clientX - bounds.left, y: event.clientY - bounds.top });

    const defaults = {};
    (PROPERTY_DEFS[type] || []).forEach(p => { if (p.key !== 'out_of_scope') defaults[p.key] = false; });
    defaults.out_of_scope = false;
    const labels = { process: 'New Process', datastore: 'New Data Store', external_entity: 'External Entity', trust_boundary: 'Trust Boundary' };
    const widths = { process: 120, datastore: 130, external_entity: 130, trust_boundary: 160 };
    const heights = { process: 120, datastore: 60, external_entity: 60, trust_boundary: 60 };

    const newNode = {
      id: `${type.slice(0, 4)}-${Date.now()}`,
      type,
      position,
      style: { width: widths[type], height: heights[type] },
      data: { label: labels[type] || 'New Element', properties: defaults, threatCount: 0, allMitigated: false },
    };
    setNodes(nds => [...nds, newNode]);
  }, [reactFlowInstance, setNodes]);

  const onNodeDoubleClick = useCallback((_, node) => {
    setEditingNode({ ...node, data: { ...node.data, properties: { ...node.data.properties } } });
    setShowNodeEditor(true);
  }, []);

  const onEdgeDoubleClick = useCallback((_, edge) => {
    setEditingEdge({ ...edge, data: { ...edge.data, properties: { ...(edge.data?.properties || {}) } } });
    setShowEdgeEditor(true);
  }, []);

  const onNodeClick = useCallback((_, node) => {
    setSelectedNodeId(prev => prev === node.id ? null : node.id);
  }, []);

  const onPaneClick = useCallback(() => {
    setSelectedNodeId(null);
  }, []);

  const saveNodeEdit = () => {
    if (!editingNode) return;
    setNodes(nds => nds.map(n => n.id === editingNode.id ? { ...n, data: { ...editingNode.data } } : n));
    setShowNodeEditor(false);
    setEditingNode(null);
  };

  const saveEdgeEdit = () => {
    if (!editingEdge) return;
    setEdges(eds => eds.map(e => e.id === editingEdge.id ? buildEdge({ ...e, label: editingEdge.label || editingEdge.data?.label, data: { ...editingEdge.data } }) : e));
    setShowEdgeEditor(false);
    setEditingEdge(null);
  };

  const deleteNode = (nodeId) => {
    setNodes(nds => nds.filter(n => n.id !== nodeId));
    setEdges(eds => eds.filter(e => e.source !== nodeId && e.target !== nodeId));
    setShowNodeEditor(false);
  };

  // Filtered threats: by STRIDE category + by selected node
  const filteredThreats = useMemo(() => {
    let filtered = threats;
    if (threatFilter !== 'all') filtered = filtered.filter(t => t.stride_category === threatFilter);
    if (selectedNodeId) filtered = filtered.filter(t => t.element_id === selectedNodeId);
    return filtered;
  }, [threats, threatFilter, selectedNodeId]);

  const exportJSON = () => {
    const data = { model: currentModel?.name || 'Unnamed', nodes, edges, threats, summary, exported_at: new Date().toISOString() };
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `threat-model-${currentModel?.name?.replace(/\s+/g, '-') || 'export'}.json`;
    a.click(); URL.revokeObjectURL(url);
  };

  const exportReport = () => {
    const html = generateHTMLReport(currentModel, nodes, edges, threats, summary);
    const blob = new Blob([html], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `threat-report-${currentModel?.name?.replace(/\s+/g, '-') || 'export'}.html`;
    a.click(); URL.revokeObjectURL(url);
  };

  // ========================
  // RENDER: Models List (no model selected)
  // ========================
  if (!currentModel) {
    return (
      <div className="space-y-6 p-6" data-testid="threat-modeling-page">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2" style={{ color: 'var(--foreground)' }}>
              <Shield className="w-6 h-6 text-blue-600" /> Threat Modeling
            </h1>
            <p className="text-sm mt-1" style={{ color: 'var(--muted-foreground)' }}>STRIDE-based threat analysis for data flow diagrams</p>
          </div>
          <Button onClick={() => setShowNewModel(true)} data-testid="create-model-btn"><Plus className="w-4 h-4 mr-1" /> New Threat Model</Button>
        </div>

        <div className="flex flex-wrap gap-2">
          {Object.entries(STRIDE_CONFIG).map(([key, cfg]) => (
            <Badge key={key} className={`${cfg.bg} ${cfg.text} ${cfg.border} border`}>{key}: {cfg.name}</Badge>
          ))}
        </div>

        {models.length === 0 ? (
          <Card><CardContent className="py-16 text-center">
            <Shield className="w-12 h-12 mx-auto text-gray-300 mb-3" />
            <p style={{ color: 'var(--muted-foreground)' }}>No threat models yet. Create one to start analyzing threats.</p>
          </CardContent></Card>
        ) : (
          <div className="grid gap-3">
            {models.map(m => (
              <Card key={m.id} className="cursor-pointer hover:shadow-md transition-shadow" data-testid={`model-card-${m.id}`}>
                <CardContent className="py-4 flex items-center justify-between">
                  <div className="flex-1 cursor-pointer" onClick={() => loadModel(m.id)}>
                    <p className="font-semibold" style={{ color: 'var(--foreground)' }}>{m.name}</p>
                    <p className="text-xs mt-0.5" style={{ color: 'var(--muted-foreground)' }}>{m.description || 'No description'}</p>
                    <div className="flex gap-3 mt-2 text-xs items-center" style={{ color: 'var(--muted-foreground)' }}>
                      <span>Threats: {m.summary?.total_threats || 0}</span>
                      {m.summary?.by_severity && Object.entries(m.summary.by_severity).filter(([, v]) => v > 0).map(([k, v]) => (
                        <Badge key={k} className={`${SEVERITY_CONFIG[k]?.bg} ${SEVERITY_CONFIG[k]?.text} text-xs`}>{k}: {v}</Badge>
                      ))}
                      <span>Created: {new Date(m.created_at).toLocaleDateString()}</span>
                    </div>
                  </div>
                  <div className="flex gap-1">
                    <Button size="sm" variant="ghost" onClick={() => loadModel(m.id)} data-testid={`view-model-${m.id}`}><Eye className="w-4 h-4" /></Button>
                    <Button size="sm" variant="ghost" onClick={() => handleDeleteModel(m.id)} className="text-red-500" data-testid={`delete-model-${m.id}`}><Trash2 className="w-4 h-4" /></Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        <Dialog open={showNewModel} onOpenChange={setShowNewModel}>
          <DialogContent>
            <DialogHeader><DialogTitle>Create Threat Model</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div><Label>Name</Label><Input value={newModelForm.name} onChange={e => setNewModelForm(p => ({ ...p, name: e.target.value }))} placeholder="e.g. Web Application Architecture" data-testid="model-name-input" /></div>
              <div><Label>Description</Label><Textarea value={newModelForm.description} onChange={e => setNewModelForm(p => ({ ...p, description: e.target.value }))} placeholder="Describe the system being modeled..." data-testid="model-desc-input" /></div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowNewModel(false)}>Cancel</Button>
              <Button onClick={handleCreateModel} disabled={loading} data-testid="create-model-submit">{loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Create'}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    );
  }

  // ========================
  // RENDER: Diagram Editor + Stencil + Threats
  // ========================
  return (
    <div className="flex flex-col h-[calc(100vh-0px)]" data-testid="threat-model-editor">
      {/* Top Bar */}
      <div className="flex items-center justify-between bg-white border-b px-4 py-2 shrink-0">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => { setCurrentModel(null); setNodes([]); setEdges([]); setThreats([]); setSummary(null); setSelectedNodeId(null); }} data-testid="back-to-models">
            <ChevronRight className="w-4 h-4 rotate-180" /> Models
          </Button>
          <div>
            <h2 className="font-semibold text-sm text-gray-900">{currentModel.name}</h2>
            <p className="text-xs text-gray-400">{currentModel.description}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button size="sm" onClick={handleGenerateThreats} disabled={generating} className="bg-blue-600 hover:bg-blue-700" data-testid="generate-threats-btn">
            {generating ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Play className="w-3.5 h-3.5 mr-1" />} Analyze
          </Button>
          <Button size="sm" variant="outline" onClick={handleSave} disabled={saving} data-testid="save-model-btn">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-3.5 h-3.5 mr-1" />} Save
          </Button>
          <div className="h-5 w-px bg-gray-200" />
          <Button size="sm" variant="outline" onClick={exportJSON} title="Export JSON" data-testid="export-json-btn"><FileJson className="w-3.5 h-3.5" /></Button>
          <Button size="sm" variant="outline" onClick={exportReport} title="Export HTML Report" data-testid="export-report-btn"><FileText className="w-3.5 h-3.5" /></Button>
          <div className="h-5 w-px bg-gray-200" />
          <Button size="sm" variant="ghost" onClick={() => setRightPanelOpen(p => !p)} data-testid="toggle-panel">
            {rightPanelOpen ? <X className="w-4 h-4" /> : <ShieldAlert className="w-4 h-4" />}
          </Button>
        </div>
      </div>

      {/* Main Content: Stencil | Canvas | Threats */}
      <div className="flex flex-1 overflow-hidden">
        {/* Left: Stencil Palette */}
        <StencilPalette />

        {/* Center: Diagram Canvas */}
        <div className="flex-1" ref={reactFlowWrapper} onDragOver={onDragOver} onDrop={onDrop}>
          <ReactFlow
            nodes={nodes}
            edges={edges}
            onNodesChange={onNodesChange}
            onEdgesChange={onEdgesChange}
            onConnect={onConnect}
            onNodeDoubleClick={onNodeDoubleClick}
            onEdgeDoubleClick={onEdgeDoubleClick}
            onNodeClick={onNodeClick}
            onPaneClick={onPaneClick}
            nodeTypes={nodeTypes}
            fitView
            className="bg-slate-50"
            defaultEdgeOptions={{
              type: 'default',
              animated: true,
              markerEnd: { type: MarkerType.ArrowClosed, color: '#64748b' },
              style: { stroke: '#64748b', strokeWidth: 1.5 },
            }}
            connectionLineStyle={{ stroke: '#3b82f6', strokeWidth: 2 }}
            snapToGrid
            snapGrid={[15, 15]}
          >
            <Background variant="dots" gap={20} size={1} color="#cbd5e1" />
            <Controls className="bg-white shadow-md rounded-lg" />
            <MiniMap
              nodeStrokeWidth={3}
              className="bg-white shadow rounded-lg"
              nodeColor={(n) => n.data?.properties?.out_of_scope ? '#d1d5db' : n.data?.allMitigated ? '#86efac' : n.data?.threatCount > 0 ? '#fca5a5' : '#93c5fd'}
            />
            {selectedNodeId && (
              <Panel position="top-center">
                <div className="bg-blue-50 border border-blue-200 rounded-lg px-3 py-1.5 text-xs text-blue-700 flex items-center gap-2 shadow-sm">
                  <Eye className="w-3.5 h-3.5" />
                  Filtering threats for selected element
                  <button className="ml-1 underline" onClick={() => setSelectedNodeId(null)}>Clear</button>
                </div>
              </Panel>
            )}
          </ReactFlow>
        </div>

        {/* Right Panel: Threats */}
        {rightPanelOpen && (
          <div className="w-[380px] bg-white border-l overflow-y-auto shrink-0" data-testid="threat-panel">
            <div className="p-3 border-b bg-slate-50">
              <div className="flex items-center justify-between mb-2">
                <h3 className="font-semibold text-sm flex items-center gap-1.5"><ShieldAlert className="w-4 h-4 text-red-500" /> STRIDE Threats</h3>
                <Badge variant="outline">{filteredThreats.length}{selectedNodeId ? ` / ${threats.length}` : ''}</Badge>
              </div>
              {summary && (
                <div className="flex flex-wrap gap-1 mb-2">
                  {Object.entries(summary.by_severity || {}).filter(([, v]) => v > 0).map(([k, v]) => (
                    <Badge key={k} className={`${SEVERITY_CONFIG[k]?.bg} ${SEVERITY_CONFIG[k]?.text} text-xs`}>{k}: {v}</Badge>
                  ))}
                </div>
              )}
              <div className="flex flex-wrap gap-1">
                <Badge variant={threatFilter === 'all' ? 'default' : 'outline'} className="cursor-pointer text-xs" onClick={() => setThreatFilter('all')}>All</Badge>
                {Object.entries(STRIDE_CONFIG).map(([key, cfg]) => (
                  <Badge key={key} variant={threatFilter === key ? 'default' : 'outline'} className={`cursor-pointer text-xs ${threatFilter === key ? '' : `${cfg.bg} ${cfg.text}`}`} onClick={() => setThreatFilter(key)}>{key}</Badge>
                ))}
              </div>
            </div>

            <div className="divide-y">
              {filteredThreats.length === 0 && (
                <div className="p-6 text-center text-gray-400 text-sm">
                  {threats.length === 0 ? 'Click "Analyze" to generate threats' : selectedNodeId ? 'No threats for selected element' : 'No threats match filter'}
                </div>
              )}
              {filteredThreats.map(t => (
                <div
                  key={t.id}
                  className={`p-3 hover:bg-slate-50 cursor-pointer transition-colors ${selectedThreat?.id === t.id ? 'bg-blue-50 border-l-2 border-blue-500' : ''}`}
                  onClick={() => setSelectedThreat(selectedThreat?.id === t.id ? null : t)}
                  data-testid={`threat-item-${t.id}`}
                >
                  <div className="flex items-start gap-2">
                    <Badge className={`${STRIDE_CONFIG[t.stride_category]?.bg} ${STRIDE_CONFIG[t.stride_category]?.text} text-xs shrink-0 mt-0.5`}>{t.stride_category}</Badge>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1.5 flex-wrap">
                        <Badge className={`${SEVERITY_CONFIG[t.severity]?.bg} ${SEVERITY_CONFIG[t.severity]?.text} text-xs`}>{t.severity}</Badge>
                        <span className="text-xs text-gray-400 truncate">{t.element_label}</span>
                        {t.status === 'mitigated' && <CheckCircle2 className="w-3 h-3 text-green-500" />}
                      </div>
                      <p className="text-xs text-gray-700 mt-1 line-clamp-2">{t.threat}</p>
                    </div>
                    <span className="text-xs font-bold text-gray-500 shrink-0">{t.dread?.average}</span>
                  </div>
                  {selectedThreat?.id === t.id && (
                    <div className="mt-3 space-y-2 pl-7" data-testid="threat-detail">
                      <div className="text-xs"><span className="font-medium text-gray-500">Rule:</span> <span className="text-gray-600">{t.rule_id}</span></div>
                      <div className="text-xs"><span className="font-medium text-gray-500">Condition:</span> <span className="text-gray-600">{t.condition}</span></div>
                      <div className="bg-emerald-50 border border-emerald-200 rounded p-2">
                        <p className="text-xs font-medium text-emerald-700 mb-0.5">Mitigation</p>
                        <p className="text-xs text-emerald-600">{t.mitigation}</p>
                      </div>
                      <div>
                        <p className="text-xs font-medium text-gray-500 mb-1">DREAD Score: {t.dread?.total}/50 (avg {t.dread?.average})</p>
                        <div className="grid grid-cols-5 gap-1">
                          {['D', 'R', 'E', 'A', 'Di'].map(d => (
                            <div key={d} className="text-center">
                              <div className={`text-xs font-bold ${(t.dread?.scores?.[d] || 0) >= 8 ? 'text-red-600' : (t.dread?.scores?.[d] || 0) >= 5 ? 'text-orange-500' : 'text-green-600'}`}>{t.dread?.scores?.[d] || 0}</div>
                              <div className="text-[9px] text-gray-400">{d === 'Di' ? 'Disc.' : d === 'D' ? 'Dam.' : d === 'R' ? 'Rep.' : d === 'E' ? 'Exp.' : 'Aff.'}</div>
                            </div>
                          ))}
                        </div>
                      </div>
                      <div className="flex gap-1 pt-1 flex-wrap">
                        {['open', 'mitigated', 'accepted', 'transferred'].map(s => (
                          <Button key={s} size="sm" variant={t.status === s ? 'default' : 'outline'} className="text-xs h-6 px-2" onClick={(e) => { e.stopPropagation(); handleThreatStatusUpdate(t.id, s); }} data-testid={`status-${s}-${t.id}`}>{s}</Button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Node Editor Dialog */}
      <Dialog open={showNodeEditor} onOpenChange={setShowNodeEditor}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Edit {editingNode?.type?.replace('_', ' ')} Element</DialogTitle></DialogHeader>
          {editingNode && (
            <div className="space-y-3 max-h-[400px] overflow-y-auto">
              <div><Label>Label</Label><Input value={editingNode.data.label} onChange={e => setEditingNode({ ...editingNode, data: { ...editingNode.data, label: e.target.value } })} data-testid="node-label-input" /></div>
              <div className="border-t pt-2">
                <p className="text-sm font-medium text-gray-700 mb-2">Security Properties</p>
                {(PROPERTY_DEFS[editingNode.type] || []).map(prop => (
                  <div key={prop.key} className="flex items-center justify-between py-1.5">
                    <Label className={`text-sm ${prop.key === 'out_of_scope' ? 'text-orange-600 font-medium' : 'text-gray-600'}`}>{prop.label}</Label>
                    <Switch checked={editingNode.data.properties?.[prop.key] || false} onCheckedChange={v => setEditingNode({ ...editingNode, data: { ...editingNode.data, properties: { ...editingNode.data.properties, [prop.key]: v } } })} data-testid={`prop-${prop.key}`} />
                  </div>
                ))}
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="destructive" size="sm" onClick={() => deleteNode(editingNode?.id)} data-testid="delete-node-btn"><Trash2 className="w-3.5 h-3.5 mr-1" /> Delete</Button>
            <Button variant="outline" onClick={() => setShowNodeEditor(false)}>Cancel</Button>
            <Button onClick={saveNodeEdit} data-testid="save-node-btn">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edge Editor Dialog */}
      <Dialog open={showEdgeEditor} onOpenChange={setShowEdgeEditor}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Edit Data Flow</DialogTitle></DialogHeader>
          {editingEdge && (
            <div className="space-y-3 max-h-[400px] overflow-y-auto">
              <div><Label>Label</Label><Input value={editingEdge.label || editingEdge.data?.label || ''} onChange={e => setEditingEdge({ ...editingEdge, label: e.target.value, data: { ...editingEdge.data, label: e.target.value } })} data-testid="edge-label-input" /></div>
              <div className="border-t pt-2">
                <p className="text-sm font-medium text-gray-700 mb-2">Flow Properties</p>
                {(PROPERTY_DEFS.dataflow || []).map(prop => (
                  <div key={prop.key} className="flex items-center justify-between py-1.5">
                    <Label className={`text-sm ${prop.key === 'bidirectional' ? 'text-blue-600 font-medium' : 'text-gray-600'}`}>
                      {prop.key === 'bidirectional' && <ArrowLeftRight className="w-3.5 h-3.5 inline mr-1" />}
                      {prop.label}
                    </Label>
                    <Switch checked={editingEdge.data?.properties?.[prop.key] || false} onCheckedChange={v => setEditingEdge({ ...editingEdge, data: { ...editingEdge.data, properties: { ...(editingEdge.data?.properties || {}), [prop.key]: v } } })} data-testid={`edge-prop-${prop.key}`} />
                  </div>
                ))}
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEdgeEditor(false)}>Cancel</Button>
            <Button onClick={saveEdgeEdit} data-testid="save-edge-btn">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// ========================
// Main Component wrapped in ReactFlowProvider
// ========================
const ThreatModelingPage = () => {
  return (
    <ReactFlowProvider>
      <ThreatModelFlow />
    </ReactFlowProvider>
  );
};

export default ThreatModelingPage;
